// store/slices/authSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, Organization, Permission, LoginRequest, LoginResponse } from '../../types';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  organization: Organization | null;
  permissions: Permission[];
  token: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  organization: null,
  permissions: [],
  token: localStorage.getItem('token'),
  loading: false,
  error: null,
};

// Async thunks
export const loginAsync = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Login failed');
      }

      localStorage.setItem('token', result.data.token);
      return result.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Login failed');
    }
  }
);

export const logoutAsync = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      localStorage.removeItem('token');
      return true;
    } catch (error) {
      // Nawet jeśli logout na serwerze się nie powiedzie, czyścimy lokalnie
      localStorage.removeItem('token');
      return true;
    }
  }
);

export const checkTokenAsync = createAsyncThunk(
  'auth/checkToken',
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return rejectWithValue('No token found');
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Token invalid');
      }

      return result.data;
    } catch (error) {
      localStorage.removeItem('token');
      return rejectWithValue('Token verification failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateUserPermissions: (state, action: PayloadAction<Permission[]>) => {
      state.permissions = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.organization = action.payload.organization;
        state.permissions = action.payload.permissions;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.user = null;
        state.organization = null;
        state.permissions = [];
        state.token = null;
      });

    // Logout
    builder
      .addCase(logoutAsync.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.organization = null;
        state.permissions = [];
        state.token = null;
        state.error = null;
      });

    // Token check
    builder
      .addCase(checkTokenAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkTokenAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.organization = action.payload.organization;
        state.permissions = action.payload.permissions;
        state.error = null;
      })
      .addCase(checkTokenAsync.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.organization = null;
        state.permissions = [];
        state.token = null;
      });
  },
});

export const { clearError, updateUserPermissions } = authSlice.actions;
export default authSlice.reducer;